/**
 * "ผูกเบอร์ … กับลูกค้า POS" under a customer's message (owner 26 ก.ย. 69: "ถ้าระบบจับได้ว่าเป็นเบอร์ให้มีปุ่มกดเชื่อม
 * ที่ข้อความได้เลย"). Phones are found in the browser (lib/phone.ts); the POS lookup is the existing
 * GET /api/v1/pos/customers with exact=1 and the link is the existing PUT /pos/conversations/:id/customer — the same
 * path as the order tab (stores/order.ts), so the order tab, history and customer card all follow at once.
 *
 * POS calls: none just for showing a button on an unlinked chat. On a linked chat the card already loads the linked
 * customer (masked number); a lookup runs only when its last 4 digits equal the phone's (to tell "ผูกแล้ว" for sure).
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { PosCustomer } from '../api/pos/types'
import { last4 } from '../lib/phone'
import { useInbox } from './inbox'
import { useOrders } from './order'

export interface Lookup {
  state: 'loading' | 'ok' | 'error'
  matches: PosCustomer[]
  error: string | null
  at: number
}

/** What the button under a message shows for one phone. */
export type PhoneStatus =
  | { kind: 'hidden' } // not a chat that can be linked (group, history, comment) or still loading
  | { kind: 'unlinked' }
  | { kind: 'checking' }
  | { kind: 'same'; name: string }
  | { kind: 'different'; name: string; customerId: string }

const LOOKUP_TTL_MS = 60_000
const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e))

export const usePhoneLink = defineStore('phonelink', () => {
  const lookups = ref<Record<string, Lookup>>({})
  const busy = ref<Record<string, boolean>>({})
  const contextAsked = new Set<string>()
  const customerAsked = new Set<string>()

  /** The chat was linked to another POS customer / unlinked — on this screen or on another one (the order store saw it in
   *  the context): a lookup that does not know the new customer may be out of date (a customer made with this number just
   *  now said "no match" before), so it is asked again; the new customer is loaded. */
  function relinked(convId: string): void {
    const cid = useOrders().contexts[convId]?.customer_id ?? null
    if (cid) {
      for (const [digits, l] of Object.entries(lookups.value)) {
        if (!l.matches.some((c) => c.id === cid)) delete lookups.value[digits]
      }
      customerAsked.delete(cid)
    }
    ensure(convId)
  }

  /** Resync (reconnect, the tab visible again — changes may have been missed): what failed or grew old is asked again
   *  the next time a button needs it (the order store reloads the open chat's context and customer itself). */
  function resync(): void {
    const orders = useOrders()
    const now = Date.now()
    for (const [digits, l] of Object.entries(lookups.value)) {
      if (l.state === 'error' || (l.state === 'ok' && now - l.at >= LOOKUP_TTL_MS)) delete lookups.value[digits]
    }
    for (const id of [...contextAsked]) if (!orders.contexts[id]) contextAsked.delete(id)
    for (const cid of [...customerAsked]) if (!orders.customers[cid]) customerAsked.delete(cid)
  }

  let offs: (() => void)[] | null = null
  function listen(): void {
    if (offs) return
    offs = [
      useOrders().onLinkChange(relinked),
      useInbox().onRealtime((e) => {
        if (!e) resync()
      }),
    ]
  }

  /** Load what the status needs (once per chat): POS context of the chat, the linked customer. */
  function ensure(convId: string): void {
    listen()
    const orders = useOrders()
    if (!orders.contexts[convId] && !contextAsked.has(convId)) {
      contextAsked.add(convId)
      void orders.refreshContext(convId).then(() => {
        if (orders.contexts[convId]) ensure(convId)
        else contextAsked.delete(convId) // failed (refreshContext keeps the error): the next showing asks again
      })
      return
    }
    const cid = orders.contexts[convId]?.customer_id
    if (cid && !orders.customers[cid] && !customerAsked.has(cid) && orders.histories[cid]?.state !== 'loading') {
      customerAsked.add(cid)
      void orders.loadHistory(cid)
    }
  }

  async function lookup(digits: string, opts: { force?: boolean } = {}): Promise<Lookup> {
    const cur = lookups.value[digits]
    if (cur && !opts.force && (cur.state === 'loading' || (cur.state === 'ok' && Date.now() - cur.at < LOOKUP_TTL_MS))) {
      return cur
    }
    lookups.value[digits] = { state: 'loading', matches: cur?.matches ?? [], error: null, at: Date.now() }
    try {
      const matches = await useOrders().searchCustomers(digits, { exactMobile: true })
      lookups.value[digits] = { state: 'ok', matches, error: null, at: Date.now() }
    } catch (e) {
      lookups.value[digits] = { state: 'error', matches: [], error: errText(e), at: Date.now() }
    }
    return lookups.value[digits]!
  }

  function status(convId: string, digits: string): PhoneStatus {
    const inbox = useInbox()
    const orders = useOrders()
    const d = inbox.details[convId]
    if (!d || d.kind !== 'dm' || d.is_history) return { kind: 'hidden' }
    const ctx = orders.contexts[convId]
    if (!ctx || !ctx.can_link) return { kind: 'hidden' }
    if (!ctx.customer_id) return { kind: 'unlinked' }
    const linked = orders.customers[ctx.customer_id]
    const found = lookups.value[digits]
    if (found?.state === 'ok') {
      if (found.matches.some((c) => c.id === ctx.customer_id)) return { kind: 'same', name: linked?.name ?? '' }
      return { kind: 'different', name: linked?.name ?? `ลูกค้า POS #${ctx.customer_id}`, customerId: ctx.customer_id }
    }
    if (!linked) return orders.histories[ctx.customer_id]?.state === 'error' ? { kind: 'unlinked' } : { kind: 'checking' }
    if (last4(linked.mobile_masked) !== last4(digits)) {
      return { kind: 'different', name: linked.name, customerId: ctx.customer_id }
    }
    // same last 4 digits: the POS says which customer really has this number (wantsLookup → lookup)
    return found?.state === 'error' ? { kind: 'unlinked' } : { kind: 'checking' }
  }

  /** The status needs a POS lookup to be sure (linked chat, same last 4 digits, not asked yet). */
  function wantsLookup(convId: string, digits: string): boolean {
    const orders = useOrders()
    const cid = orders.contexts[convId]?.customer_id
    const linked = cid ? orders.customers[cid] : null
    return !!linked && !lookups.value[digits] && last4(linked.mobile_masked) === last4(digits)
  }

  /** Link the chat to this POS customer (same call as the order tab) → toast; every panel follows. */
  async function link(convId: string, c: PosCustomer): Promise<boolean> {
    const inbox = useInbox()
    busy.value[convId] = true
    try {
      await useOrders().linkCustomer(convId, c)
      inbox.flash(`ผูกแชทนี้กับ ${c.name} แล้ว`)
      return true
    } catch (e) {
      inbox.flash(`ผูกไม่สำเร็จ: ${errText(e)}`)
      return false
    } finally {
      busy.value[convId] = false
    }
  }

  /** No POS customer with this number: the order tab's own create form, opened with it. */
  function createNew(convId: string, digits: string): void {
    const inbox = useInbox()
    useOrders().requestCreate(convId, digits)
    inbox.panelTab = 'order'
  }

  return { lookups, busy, ensure, lookup, status, wantsLookup, link, createNew }
})
